import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Globe2, HeartPulse, Truck, Target, Eye, CheckCircle2 } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { stats, heroImage } from "../mock";

const values = [
  { icon: ShieldCheck, title: "Safety & Compliance", desc: "Every shipment follows international pharmaceutical export and customs regulations." },
  { icon: Globe2, title: "Global Reach", desc: "Delivering essential medicines to 220+ countries with reliable partner networks." },
  { icon: HeartPulse, title: "Care First", desc: "We treat every parcel as a lifeline for a family member waiting overseas." },
  { icon: Truck, title: "Speed & Transparency", desc: "Guaranteed 5-6 day delivery with live tracking at every stage." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About ShipMeds | Trusted International Medicine Courier from India"
        description="ShipMeds is a premier international pharmaceutical logistics courier from India, delivering medicines to 220+ countries with care, safety and speed."
        path="/about"
      />
      <PageHero title="About ShipMeds" subtitle="Delivering health, home to your family overseas — safely, legally and on time." crumb="About Us" />
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden card-shadow">
            <img src={heroImage} alt="ShipMeds medicine courier service" className="w-full h-[380px] object-cover" />
          </div>
          <div>
            <p className="inline-block text-teal-600 font-bold uppercase tracking-widest text-xs bg-teal-50 px-4 py-1.5 rounded-full mb-4">Who We Are</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight mb-4">Your Trusted International Medicine Courier Partner</h2>
            <p className="text-slate-600 leading-relaxed mb-4">ShipMeds is a premier international pharmaceutical logistics courier based in India. We help individuals and families send all types of medicines abroad — from allopathic and ayurvedic to homeopathic and cold-chain biologics — with guaranteed doorstep delivery.</p>
            <p className="text-slate-600 leading-relaxed mb-6">Guided by a mission of care, safety and speed, we manage free doorstep pickup, compliant packing, complete customs documentation and priority dispatch so you never have to worry about a thing.</p>
            <ul className="space-y-2.5">
              {["Free doorstep pickup across India", "Documentation & customs clearance support", "Guaranteed 5-6 day delivery with live tracking", "24x7 customer support on Call & WhatsApp"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-5 h-5 text-teal-500 shrink-0 mt-0.5" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-8 border border-slate-100 card-shadow">
            <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4"><Target className="w-6 h-6" /></div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Our Mission</h3>
            <p className="text-slate-600 leading-relaxed">To make safe, legal and affordable medicine access possible for every family, no matter where in the world they live.</p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-slate-100 card-shadow">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4"><Eye className="w-6 h-6" /></div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Our Vision</h3>
            <p className="text-slate-600 leading-relaxed">To be the world's most trusted international medicine courier, known for reliability, transparency and genuine care.</p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-14">
            {stats.map((s) => (
              <div key={s.label} className="rounded-2xl border border-slate-100 card-shadow p-6 text-center bg-gradient-to-b from-white to-teal-50/40">
                <div className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">{s.value}</div>
                <div className="mt-1.5 text-sm font-medium text-slate-600">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl font-extrabold text-slate-900">What We Stand For</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-teal-200 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 text-white flex items-center justify-center mb-4"><v.icon className="w-6 h-6" /></div>
                <h4 className="font-bold text-slate-900 mb-2">{v.title}</h4>
                <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/courier-charges" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold px-7 py-3.5 rounded-lg transition-colors">Get a Free Quote</Link>
          </div>
        </div>
      </section>
    </>
  );
}
